/**
 * Left panel: active sessions + collapsible Archive folder.
 * Pure presentational - data and mutations live in useChatSessions.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Modal,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { ChatSession } from '../../services/chatApi';
import { useTheme } from '../../contexts/ThemeContext';
import { relativeTimeUnit } from '../../utils/relativeTime';
import { showConfirm } from '../../utils/alert';

export interface SessionSidebarProps {
  activeSessions: ChatSession[];
  archivedSessions: ChatSession[];
  currentSessionId: string | null;
  busy?: boolean;
  onSelect(id: string): void;
  onNewChat(): void;
  onArchive(id: string): void;
  onUnarchive(id: string): void;
  onRename(id: string, title: string): void;
  onDelete(id: string): void;
}

// Where the row menu lands when no measurement has answered yet: just below
// the chat header, hugging the right edge. Never (0, 0) — a popover pinned to
// the dead top-left corner reads as a menu that "doesn't open" (bug B,
// 2026-10-04).
const MENU_FALLBACK_ANCHOR = { top: 64, right: 12 };

// An anchor is only usable if it sits inside the window. Round 9 captured
// anchors at layout time, which races the layout commit and stored garbage
// (zeros, which the right-edge math turns into right = windowWidth) — the
// menu opened off-screen and only appeared after a resize re-measured it.
// Anything out of bounds is rejected and the visible fallback kept instead.
export function isSaneAnchor(
  anchor: { top: number; right: number },
  windowWidth: number,
  windowHeight: number
): boolean {
  return (
    anchor.top >= 0 &&
    anchor.right >= 0 &&
    anchor.right <= windowWidth &&
    anchor.top <= windowHeight
  );
}

// Press-time refine: measure the pressed "..." button in window coords and
// hand a sane anchor to `onAnchor`. Exported for tests.
//
// The measurement is read SYNCHRONOUSLY (getBoundingClientRect, web) when the
// platform exposes it, because the async measureInWindow path races a row
// remount: `Row` is an inner component (unstable identity), so the menu-open
// state change remounts the rows and DETACHES the pressed button's node —
// UIManager.measureInWindow's deferred callback then reads the detached node
// and answers all zeros. That detached-node read was round 9's garbage, and
// the browser probe (2026-10-04) reproduced it at press time too. A
// synchronous read inside the press handler cannot race the remount.
// measureInWindow remains the native path (no DOM there).
export function refineMenuAnchor(
  btn:
    | {
        getBoundingClientRect?: () => {
          left: number;
          top: number;
          width: number;
          height: number;
        } | null;
        measureInWindow?: (cb: (x: number, y: number, width: number, height: number) => void) => void;
      }
    | undefined
    | null,
  windowWidth: number,
  windowHeight: number,
  onAnchor: (anchor: { top: number; right: number }) => void
): void {
  const deliver = (x: number, y: number, width: number, height: number): boolean => {
    // A pressed button always has positive on-screen area; a zero-size rect
    // is exactly the detached-node garbage (zeros → right = windowWidth →
    // popover off-screen left). Bounds alone cannot catch it (right ==
    // windowWidth is technically "in bounds"), so drop it here.
    if (width <= 0 || height <= 0) return false;
    const anchor = { top: y + height, right: windowWidth - x - width };
    if (isSaneAnchor(anchor, windowWidth, windowHeight)) {
      onAnchor(anchor);
      return true;
    }
    return false;
  };

  const rect = btn?.getBoundingClientRect?.();
  if (rect && deliver(rect.left, rect.top, rect.width, rect.height)) return;
  // Native (and any web fallback): the deferred bridge read. It only ever
  // REFINES — the caller has already opened the menu on the visible fallback.
  btn?.measureInWindow?.((x, y, width, height) => {
    deliver(x, y, width, height);
  });
}

export function SessionSidebar(props: SessionSidebarProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [menuSession, setMenuSession] = useState<ChatSession | null>(null);
  const [menuArchived, setMenuArchived] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(MENU_FALLBACK_ANCHOR);
  const menuBtnRefs = useRef<Map<string, any>>(new Map());
  const [renameFor, setRenameFor] = useState<ChatSession | null>(null);
  const [renameText, setRenameText] = useState('');
  // Every anchor is validated at use time: an anchor that fell out of bounds
  // (e.g. the window shrank after the refine answered) degrades to the
  // visible fallback.
  const anchor = isSaneAnchor(menuAnchor, windowWidth, windowHeight)
    ? menuAnchor
    : MENU_FALLBACK_ANCHOR;

  const lastActivity = (iso: string) => {
    const rt = relativeTimeUnit(iso);
    return rt.unit === 'justNow'
      ? t('chat.sessions.lastActivity.justNow')
      : t(`chat.sessions.lastActivity.${rt.unit}`, { n: rt.n });
  };

  const confirmDelete = async (target: ChatSession) => {
    const ok = await showConfirm(t('chat.sessions.deleteTitle'), t('chat.sessions.deleteMessage'), {
      destructiveAction: true,
      confirmText: t('chat.sessions.delete'),
    });
    if (ok) props.onDelete(target.id);
  };

  const Row = ({ item, archived }: { item: ChatSession; archived: boolean }) => (
    <View style={styles.rowWrap}>
      <Pressable
        testID={`session-row-${item.id}`}
        onPress={() => {
          props.onSelect(item.id);
          setMenuSession(null);
        }}
        style={[styles.row, props.currentSessionId === item.id && styles.rowCurrent]}
      >
        <View style={styles.rowTitleLine}>
          <Text style={styles.rowTitle} numberOfLines={1}>
            {item.title ?? t('chat.newSessionTitle')}
          </Text>
          {/* ◔ unsaved artifacts (spec §6.4): an open thesis draft / in-flight
              import in this session — a nudge to come back and finish it.
              The `target_price_computed` projection (spec §3) is NOT a draft
              — it is ground truth with no terminal receipt, so counting it
              would badge every finished build forever. */}
          {(item.unsaved_artifacts?.some((a) => a.kind !== 'target_price_computed') ?? false) && (
            <Text
              testID={`session-draft-${item.id}`}
              accessibilityLabel={t('chat.sessions.unsavedDraft', {
                defaultValue: 'Has unsaved drafts',
              })}
              style={styles.rowBadge}
            >
              ◔
            </Text>
          )}
        </View>
        <Text style={styles.rowTime}>{lastActivity(item.last_message_at)}</Text>
      </Pressable>
      {/* is_main sessions refuse rename/archive/delete server-side (403) —
          don't render a menu that can only fail. */}
      {!item.is_main && (
        <Pressable
          testID={`session-menu-${item.id}`}
          ref={(ref) => {
            if (ref) menuBtnRefs.current.set(item.id, ref);
            else menuBtnRefs.current.delete(item.id);
          }}
          onPress={() => {
            // ANCHOR FLOW IS PRESS-TIME ONLY. Round 9 captured anchors at layout
            // time (onLayout → measureInWindow); that races the layout commit on
            // web, stores garbage, and the menu only became visible after a
            // resize re-measured it (2026-10-04, second user report). Now the
            // press opens the modal on the visible fallback FIRST — the very
            // first frame is always sane — and an immediate measureInWindow on
            // the pressed button only refines the anchor when it answers with
            // something in-bounds. The callback is async (or never fires), so it
            // must refine, never gate.
            const opening = menuSession?.id !== item.id;
            setMenuSession(opening ? item : null);
            setMenuArchived(archived);
            setMenuAnchor(MENU_FALLBACK_ANCHOR);
            if (opening) {
              refineMenuAnchor(
                menuBtnRefs.current.get(item.id),
                windowWidth,
                windowHeight,
                setMenuAnchor
              );
            }
          }}
          style={styles.menuBtn}
          hitSlop={8}
        >
          <Text style={styles.menuDots}>...</Text>
        </Pressable>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <Pressable onPress={props.onNewChat} style={styles.newChatBtn} disabled={props.busy}>
        {props.busy ? (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        ) : (
          <Text style={styles.newChatText}>+ {t('chat.newChat')}</Text>
        )}
      </Pressable>
      <ScrollView style={styles.list}>
        {props.activeSessions.map((item) => (
          <Row key={item.id} item={item} archived={false} />
        ))}
      </ScrollView>
      <View style={styles.archiveSection}>
        <Pressable onPress={() => setArchiveOpen(!archiveOpen)} style={styles.archiveHeader}>
          <Text style={styles.archiveHeaderText}>{t('chat.sessions.archiveFolder')}</Text>
          <Text style={styles.archiveHeaderText}>{archiveOpen ? 'v' : '>'}</Text>
        </Pressable>
        {archiveOpen &&
          (props.archivedSessions.length === 0 ? (
            <Text style={styles.emptyArchive}>{t('chat.sessions.emptyArchive')}</Text>
          ) : (
            props.archivedSessions.map((item) => <Row key={item.id} item={item} archived />)
          ))}
      </View>

      <Modal visible={renameFor !== null} transparent onRequestClose={() => setRenameFor(null)}>
        <View style={styles.dialogScrim}>
          <View style={styles.dialog}>
            <Text style={styles.dialogTitle}>{t('chat.sessions.renameTitle')}</Text>
            <TextInput
              testID="rename-input"
              value={renameText}
              onChangeText={setRenameText}
              placeholder={t('chat.sessions.renamePlaceholder')}
              placeholderTextColor={theme.colors.textSecondary}
              style={styles.dialogInput}
            />
            <View style={styles.dialogActions}>
              <Pressable onPress={() => setRenameFor(null)} style={styles.dialogBtn}>
                <Text style={styles.dialogBtnCancel}>{t('chat.sessions.cancel')}</Text>
              </Pressable>
              <Pressable
                disabled={!renameText.trim()}
                onPress={() => {
                  if (renameFor) props.onRename(renameFor.id, renameText.trim());
                  setRenameFor(null);
                }}
                style={[styles.dialogBtn, !renameText.trim() && styles.dialogBtnDisabled]}
              >
                <Text style={styles.dialogBtnSave}>{t('chat.sessions.save')}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={menuSession !== null} transparent onRequestClose={() => setMenuSession(null)}>
        <Pressable style={styles.menuScrim} onPress={() => setMenuSession(null)}>
          <View
            testID="session-menu-popover"
            style={[
              styles.menuPopover,
              // First frame: the visible below-header fallback. The pressed
              // button's measureInWindow refines this in place when it answers
              // with an in-bounds position — never the dead (0, 0) corner.
              {
                position: 'absolute',
                top: anchor.top,
                right: anchor.right,
              },
            ]}
          >
            {menuSession && (
              <>
                <Pressable
                  onPress={() => {
                    setRenameFor(menuSession);
                    setRenameText(menuSession.title ?? '');
                    setMenuSession(null);
                  }}
                  style={styles.menuItem}
                >
                  <Text style={styles.menuItemText}>{t('chat.sessions.rename')}</Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    if (menuArchived) props.onUnarchive(menuSession.id);
                    else props.onArchive(menuSession.id);
                    setMenuSession(null);
                  }}
                  style={styles.menuItem}
                >
                  <Text style={styles.menuItemText}>
                    {menuArchived ? t('chat.sessions.unarchive') : t('chat.sessions.archive')}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    setMenuSession(null);
                    void confirmDelete(menuSession);
                  }}
                  style={styles.menuItem}
                >
                  <Text style={[styles.menuItemText, styles.menuItemDestructive]}>
                    {t('chat.sessions.delete')}
                  </Text>
                </Pressable>
              </>
            )}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: { flex: 1, padding: theme.spacing.sm },
    newChatBtn: {
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
    },
    newChatText: { color: theme.colors.textInverse, fontWeight: '600' },
    list: { flex: 1 },
    rowWrap: { position: 'relative' },
    row: {
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.radii.md,
      borderLeftWidth: 3,
      borderLeftColor: 'transparent',
    },
    rowCurrent: {
      backgroundColor: theme.colors.surfaceSecondary,
      borderLeftColor: theme.colors.primary,
    },
    rowTitle: { color: theme.colors.text, fontSize: theme.typography.fontSizeMd },
    rowTitleLine: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    rowBadge: {
      color: theme.colors.primary,
      fontSize: theme.typography.fontSizeMd,
    },
    rowTime: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeSm,
      marginTop: 2,
    },
    menuBtn: {
      position: 'absolute',
      right: theme.spacing.xs,
      top: theme.spacing.sm,
      padding: theme.spacing.xs,
    },
    menuDots: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSizeMd },
    menuScrim: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    menuPopover: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      borderColor: theme.colors.border,
      elevation: 4,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      minWidth: 140,
    },
    menuItem: { paddingVertical: theme.spacing.sm, paddingHorizontal: theme.spacing.md },
    menuItemText: { color: theme.colors.text, fontSize: theme.typography.fontSizeMd },
    menuItemDestructive: { color: theme.colors.error ?? theme.colors.primary },
    archiveSection: {
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      paddingTop: theme.spacing.xs,
    },
    archiveHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      padding: theme.spacing.sm,
    },
    archiveHeaderText: { color: theme.colors.textSecondary, fontWeight: '600' },
    emptyArchive: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeSm,
      paddingHorizontal: theme.spacing.sm,
      paddingBottom: theme.spacing.sm,
    },
    dialogScrim: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.4)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    dialog: {
      width: '80%',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.md,
    },
    dialogTitle: {
      color: theme.colors.text,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing.sm,
    },
    dialogInput: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: theme.radii.md,
      padding: theme.spacing.sm,
      color: theme.colors.text,
    },
    dialogActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: theme.spacing.md,
      gap: theme.spacing.sm,
    },
    dialogBtn: { padding: theme.spacing.sm },
    dialogBtnCancel: { color: theme.colors.textSecondary },
    dialogBtnSave: { color: theme.colors.primary, fontWeight: '600' },
    dialogBtnDisabled: { opacity: 0.5 },
  });
}
